// Site header behaviour:
//   - Off-canvas mobile nav (<dialog>) open/close
//   - Predictive search dropdown (desktop search input)
//
// The inline desktop nav was removed in favour of a hamburger-driven
// drawer at all breakpoints (Toy Company has 17+ top-level categories;
// horizontal nav couldn't tell the catalogue's story).
//
// Lives in /assets (not in `{% javascript %}`) because the CLI dev server's
// section-JS bundling has hot-reload quirks; an explicit <script src=…> tag
// is reliable in both local and hosted environments.

// ===== Off-canvas mobile nav =====
function initMobileNav() {
  const dialog = document.getElementById('site-mobile-nav');
  const triggers = document.querySelectorAll('[data-mobile-nav-trigger]');
  const closers = document.querySelectorAll('[data-mobile-nav-close]');

  if (!dialog || typeof dialog.showModal !== 'function') return;

  triggers.forEach((btn) => {
    btn.addEventListener('click', (event) => {
      event.preventDefault();
      try {
        dialog.showModal();
        btn.setAttribute('aria-expanded', 'true');
      } catch (error) {
        // Fallback: setting the attribute opens it non-modally. Better
        // than nothing if showModal somehow throws (already-open, etc).
        dialog.setAttribute('open', '');
      }
    });
  });

  closers.forEach((btn) => {
    btn.addEventListener('click', () => dialog.close());
  });

  // Click on the backdrop (event.target is the dialog itself, not a child)
  // closes the drawer.
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });

  dialog.addEventListener('close', () => {
    triggers.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
  });
}

// ===== Predictive search =====
// Mirrors the mobile app pattern: debounced fetch as user types,
// product results in a dropdown, click navigates to PDP.
//
// Uses Shopify's built-in /search/suggest.json — no token/CORS concerns,
// server-side rate-limited, returns the shop's products / pages / articles.

const PREDICTIVE_SEARCH_DEBOUNCE_MS = 220;
const PREDICTIVE_SEARCH_MIN_QUERY = 2;
const PREDICTIVE_SEARCH_LIMIT = 6;
const PREDICTIVE_SEARCH_QUERY_LIMIT = 4;

// Storewide sale % (0 = off), rendered server-side on the search form when the
// sale is live. Lets the predictive dropdown show the same discounted price.
const SALE_PERCENT = parseInt(
  document.querySelector('[data-predictive-search]')?.dataset.salePercent || '0',
  10,
) || 0;

function escapeHtml(str) {
  return String(str).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  })[c]);
}

function formatMoney(price) {
  const num = parseFloat(price);
  if (Number.isNaN(num)) return price;
  const locale = (window.Shopify && window.Shopify.locale) || 'en-PK';
  const currency =
    (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'PKR';
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `Rs ${num.toLocaleString('en-PK')}`;
  }
}

function predictiveResultsHtml(products, queries, query) {
  const searchIcon =
    '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7"></circle><line x1="21" y1="21" x2="16.5" y2="16.5"></line></svg>';
  // Compact 16px icons for the "Suggested" rows (mirrors the app: search + up-left arrow).
  const smallSearchIcon =
    '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7"></circle><line x1="21" y1="21" x2="16.5" y2="16.5"></line></svg>';
  const arrowIcon =
    '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="17" y1="17" x2="7" y2="7"></line><polyline points="7 17 7 7 17 7"></polyline></svg>';
  const searchFor = `
    <a class="predictive-search__query" href="/search?q=${encodeURIComponent(query)}">
      <span class="predictive-search__query-icon">${searchIcon}</span>
      <span>Search for "${escapeHtml(query)}"</span>
    </a>
  `;

  // "Suggested" — query-term suggestions (mirrors the mobile app's Suggested section).
  let suggestedHtml = '';
  if (queries.length) {
    const rows = queries
      .slice(0, PREDICTIVE_SEARCH_QUERY_LIMIT)
      .map((s) => {
        const text = s.text || '';
        const url = s.url || `/search?q=${encodeURIComponent(text)}`;
        return `
          <a class="predictive-search__suggestion" href="${escapeHtml(url)}" role="option">
            <span class="predictive-search__suggestion-icon">${smallSearchIcon}</span>
            <span class="predictive-search__suggestion-text">${escapeHtml(text)}</span>
            <span class="predictive-search__suggestion-arrow">${arrowIcon}</span>
          </a>
        `;
      })
      .join('');
    suggestedHtml = `
      <div class="predictive-search__group">
        <p class="predictive-search__group-title">Suggested</p>
        ${rows}
      </div>
    `;
  }

  // "Top products" — the product matches.
  let productsHtml = '';
  if (products.length) {
    const items = products
      .map((p) => {
        const thumb = p.image
          ? `<img class="predictive-search__thumb" src="${escapeHtml(p.image)}" alt="" loading="lazy" width="44" height="44">`
          : `<div class="predictive-search__thumb"></div>`;
        let priceVal = parseFloat(p.price);
        let compareVal = parseFloat(p.compare_at_price_min);
        let onSale = Number.isFinite(priceVal) && Number.isFinite(compareVal) && compareVal > priceVal;
        // Storewide sale display: mirror the card/PDP treatment in the dropdown.
        if (SALE_PERCENT > 0 && Number.isFinite(priceVal)) {
          compareVal = priceVal;
          priceVal = (priceVal * (100 - SALE_PERCENT)) / 100;
          onSale = true;
        }
        const compareHtml = onSale
          ? `<s class="predictive-search__compare">${formatMoney(compareVal)}</s>`
          : '';
        return `
          <a href="${escapeHtml(p.url)}" class="predictive-search__result" role="option">
            ${thumb}
            <div class="predictive-search__info">
              <div class="predictive-search__title">${escapeHtml(p.title)}</div>
            </div>
            <div class="predictive-search__prices">
              ${compareHtml}
              <span class="predictive-search__price${onSale ? ' predictive-search__price--sale' : ''}">${formatMoney(priceVal)}</span>
            </div>
          </a>
        `;
      })
      .join('');
    productsHtml = `
      <div class="predictive-search__group">
        <p class="predictive-search__group-title">Top products</p>
        ${items}
      </div>
    `;
  }

  if (!queries.length && !products.length) {
    return (
      searchFor +
      `<div class="predictive-search__status">No matches for "${escapeHtml(query)}"</div>`
    );
  }

  // App order: "Search for X" → Suggested → Top products.
  return searchFor + suggestedHtml + productsHtml;
}

// Shared predictive controller for an input + results panel. Powers both the
// desktop inline dropdown and the mobile full-screen dialog.
function createPredictive(input, panel) {
  let abortController = null;
  let debounceTimer = null;

  function show(html) {
    panel.innerHTML = html;
    panel.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  }

  function hide() {
    panel.hidden = true;
    panel.innerHTML = '';
    input.setAttribute('aria-expanded', 'false');
  }

  async function fetchSuggestions(query) {
    if (abortController) abortController.abort();
    abortController = new AbortController();

    const params = new URLSearchParams({
      q: query,
      // Query-term suggestions ("Suggested") + products ("Top products"), like the
      // mobile app. limit_scope=each → the limit applies per resource type.
      'resources[type]': 'query,product',
      'resources[limit]': String(PREDICTIVE_SEARCH_LIMIT),
      'resources[limit_scope]': 'each',
      'resources[options][fields]': 'title,vendor,product_type,tag',
    });

    try {
      const res = await fetch(`/search/suggest.json?${params}`, { signal: abortController.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const results = (data && data.resources && data.resources.results) || {};
      show(predictiveResultsHtml(results.products || [], results.queries || [], query));
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('[site-header] predictive search failed:', error);
      hide();
    }
  }

  // Wire typing. `onChange(query)` runs every keystroke (the dialog uses it to
  // toggle its suggestions + clear button).
  function listen(onChange) {
    input.addEventListener('input', () => {
      const query = input.value.trim();
      clearTimeout(debounceTimer);
      if (onChange) onChange(query);
      if (query.length < PREDICTIVE_SEARCH_MIN_QUERY) {
        hide();
        return;
      }
      show('<div class="predictive-search__status">Searching…</div>');
      debounceTimer = setTimeout(() => fetchSuggestions(query), PREDICTIVE_SEARCH_DEBOUNCE_MS);
    });
  }

  return { show, hide, listen };
}

// Desktop: inline dropdown anchored under the header search input.
function wirePredictiveSearch(form) {
  const input = form.querySelector('input[type="search"]');
  const panel = form.querySelector('[data-search-panel]');
  if (!input || !panel) return;

  const ps = createPredictive(input, panel);
  ps.listen();

  input.addEventListener('focus', () => {
    if (input.value.trim().length >= PREDICTIVE_SEARCH_MIN_QUERY && panel.innerHTML !== '') {
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }
  });

  document.addEventListener('click', (event) => {
    if (!form.contains(event.target)) ps.hide();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || panel.hidden) return;
    if (!form.contains(document.activeElement)) return;
    ps.hide();
    input.focus();
  });

  input.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowDown' && !panel.hidden) {
      const first = panel.querySelector('a[href]');
      if (first) {
        event.preventDefault();
        first.focus();
      }
    }
  });
}

// ===== Mobile full-screen search dialog =====
// On mobile the header search input is a launcher (readonly so it doesn't raise
// the keyboard); clicking it opens this full-screen <dialog> with its own input,
// suggestion chips, and predictive results. Desktop keeps the inline dropdown.
function initSearchDialog() {
  const dialog = document.getElementById('search-dialog');
  const trigger = document.querySelector('[data-search-trigger]');
  if (!dialog || !trigger || typeof dialog.showModal !== 'function') return;

  const input = dialog.querySelector('#search-dialog-input');
  const panel = dialog.querySelector('[data-search-panel]');
  const suggestions = dialog.querySelector('[data-search-suggestions]');
  const closeBtn = dialog.querySelector('[data-search-dialog-close]');
  const clearBtn = dialog.querySelector('[data-search-dialog-clear]');
  if (!input || !panel) return;

  const mobile = window.matchMedia('(max-width: 767px)');
  const ps = createPredictive(input, panel);

  function syncTrigger() {
    if (mobile.matches) trigger.setAttribute('readonly', '');
    else trigger.removeAttribute('readonly');
  }
  syncTrigger();
  mobile.addEventListener('change', syncTrigger);

  function reset() {
    input.value = '';
    ps.hide();
    if (suggestions) suggestions.hidden = false;
    if (clearBtn) clearBtn.hidden = true;
  }

  trigger.addEventListener('click', (event) => {
    if (!mobile.matches) return; // desktop uses the inline dropdown
    event.preventDefault();
    dialog.showModal();
    requestAnimationFrame(() => input.focus());
  });

  ps.listen((query) => {
    const hasQuery = query.length > 0;
    if (suggestions) suggestions.hidden = hasQuery;
    if (clearBtn) clearBtn.hidden = !hasQuery;
  });

  if (clearBtn) {
    clearBtn.addEventListener('click', () => {
      reset();
      input.focus();
    });
  }
  if (closeBtn) closeBtn.addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });
  dialog.addEventListener('close', reset);
}

function initPredictiveSearch() {
  document.querySelectorAll('[data-predictive-search]').forEach(wirePredictiveSearch);
}

function initSiteHeader() {
  initMobileNav();
  initPredictiveSearch();
  initSearchDialog();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSiteHeader);
} else {
  initSiteHeader();
}
